import {
  ArrowRight,
  Box,
  Briefcase,
  CalendarDays,
  ClipboardList,
  Code2,
  createIcons,
  FilePenLine,
  Info,
  LineChart,
  Mail,
  Menu,
  MessageSquare,
  Network,
  Pencil,
  PieChart,
  Route,
  Search,
  Shield,
  ShieldCheck,
  Tag,
  Target,
  TrendingDown,
  TrendingUp,
  UserRound,
  Users,
  X
} from "lucide";
import {
  aboutPage,
  article,
  audienceCards,
  careerPage,
  contactDirections,
  contactTopics,
  footerGroups,
  freeReportPreview,
  hero,
  homeInsights,
  insights,
  navItems,
  packageModules,
  packagePlans,
  samplePage,
  scannerDemo,
  site,
  valueCards,
  valueSection
} from "./content";
import { type ContactPayload, validateContactForm, validateUpgradeReservationEmail } from "./form";
import privacyPolicyMarkdown from "./legal/privacy-policy.md?raw";
import userServiceAgreementMarkdown from "./legal/user-service-agreement.md?raw";
import "./styles.css";

const app = document.querySelector<HTMLDivElement>("#app");

if (!app) {
  throw new Error("Missing #app container");
}

const root = app;

const routeAliases: Record<string, string> = {
  home: "home",
  products: "career-upgrade",
  services: "career-upgrade",
  "sample-report-section": "sample-report",
  sampleReport: "sample-report",
  upgradePackage: "upgrade-reservation",
  upgradeReservation: "upgrade-reservation",
  "article-workflow-first-step": "article-task-value-change",
  "article-task-ai-fit": "article-task-value-change",
  privacyPolicy: "privacy",
  userAgreement: "terms",
  serviceAgreement: "terms",
  termsOfService: "terms"
};

const pageRoutes = new Set([
  "home",
  "career-upgrade",
  "sample-report",
  "insights",
  "article-task-value-change",
  "upgrade-reservation",
  "about",
  "contact",
  "terms",
  "privacy"
]);

const routeMetadata: Record<string, { title: string; description: string }> = {
  home: {
    title: "AI岗位风险评估与升值初筛｜知汐 Labs",
    description:
      "知汐 Labs 提供 AI 岗位升值初筛，帮助你看清岗位中正在贬值的任务、正在升值的能力，并生成第一个可执行的 AI 工作流动作。"
  },
  "career-upgrade": {
    title: "免费AI岗位升值初筛｜岗位风险评估与工作流建议｜知汐 Labs",
    description:
      "通过知汐 Labs 岗位 AI 升值初筛，输入岗位与主要工作内容，识别岗位风险、任务价值变化与第一个可执行的 AI 工作流建议。"
  },
  "sample-report": {
    title: "AI岗位升值报告样例｜免费初筛与完整升级包｜知汐 Labs",
    description:
      "查看知汐 Labs 岗位 AI 升值报告样例，了解免费初筛报告与完整岗位 AI 升级包的内容差异、分析深度和交付模块。"
  },
  "upgrade-reservation": {
    title: "完整岗位AI升级包预约｜知汐 Labs",
    description:
      "预约知汐 Labs 完整岗位 AI 升级包，获取更完整的岗位 AI 上手动作、材料清单、AI 指令链、输出模板与行动计划。"
  },
  insights: {
    title: "岗位升值观察｜AI时代任务价值变化与工作流方法｜知汐 Labs",
    description:
      "阅读知汐 Labs 岗位升值观察，了解 AI 时代岗位任务价值变化、工作流方法和个人职业升级的可复用判断方式。"
  },
  "article-task-value-change": {
    title: "AI时代岗位真正变化的不是名称而是任务价值｜知汐 Labs",
    description:
      "从任务价值角度理解 AI 时代岗位变化：岗位名称不一定消失，但任务结构、能力要求和工作流会持续重组。"
  },
  about: {
    title: "关于知汐 Labs｜AI岗位价值评估与工作流平台",
    description:
      "了解知汐 Labs 如何通过岗位任务价值分析，帮助个人看清 AI 时代的任务变化，并把下一步变成可执行的工作流。"
  },
  contact: {
    title: "联系知汐 Labs｜产品反馈、报告支持与合作咨询",
    description:
      "联系知汐 Labs，提交产品反馈、报告支持、商务合作或其他咨询，我们会根据你的留言进行后续回复。"
  },
  privacy: {
    title: "隐私政策｜知汐 Labs",
    description: "查看知汐 Labs 隐私政策，了解我们如何收集、使用、存储、共享和保护你的个人信息。"
  },
  terms: {
    title: "用户协议｜知汐 Labs",
    description: "查看知汐 Labs 用户协议，了解产品服务范围、使用规范、免责声明、知识产权和联系方式。"
  }
};

const legalDocuments = {
  terms: {
    title: "用户协议",
    label: "用户协议",
    markdown: userServiceAgreementMarkdown,
    icon: "ShieldCheck"
  },
  privacy: {
    title: "隐私政策",
    label: "隐私政策",
    markdown: privacyPolicyMarkdown,
    icon: "Shield"
  }
} as const;

function icon(name: string, className = "icon"): string {
  const dataName = name.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();

  return `<i class="${className}" data-lucide="${dataName}"></i>`;
}

function currentRoute(): string {
  const hash = window.location.hash.replace("#", "") || "home";
  const route = routeAliases[hash] ?? hash;

  return pageRoutes.has(route) ? route : "home";
}

function activeRoute(route: string): string {
  if (route === "article-task-value-change") {
    return "insights";
  }

  if (route === "upgrade-reservation") {
    return "sample-report";
  }

  if (route === "terms") {
    return "";
  }

  return route;
}

function syncDocumentMetadata(route: string): void {
  const metadata = routeMetadata[route] ?? routeMetadata.home;
  let description = document.querySelector<HTMLMetaElement>('meta[name="description"]');

  document.title = metadata.title;

  if (!description) {
    description = document.createElement("meta");
    description.name = "description";
    document.head.append(description);
  }

  description.content = metadata.description;
}

function renderLogo(): string {
  return `
    <span class="brand-mark" aria-hidden="true"><img src="${site.logo}" alt="" /></span>
    <span>${site.brand}</span>
  `;
}

function renderNav(route: string): string {
  const current = activeRoute(route);
  const ctaLabel = route === "sample-report" || route === "career-upgrade" ? "免费开始初筛" : "免费测我的岗位";
  const links = navItems
    .map((item) => {
      const itemRoute = item.href.replace("#", "");
      const activeClass = itemRoute === current ? " is-active" : "";

      return `<a class="nav-link${activeClass}" href="${item.href}">${item.label}</a>`;
    })
    .join("");

  return `
    <header class="site-header" data-header>
      <div class="header-inner">
        <a class="brand" href="#home" aria-label="${site.brand} 首页">${renderLogo()}</a>
        <nav class="desktop-nav" aria-label="主导航">${links}</nav>
        <a class="nav-cta" href="${site.personalDiagnosisUrl}">${ctaLabel}</a>
        <button class="menu-button" type="button" aria-label="打开菜单" data-menu-toggle>${icon("Menu")}</button>
      </div>
      <div class="mobile-panel" data-mobile-panel>
        <button class="menu-close" type="button" aria-label="关闭菜单" data-menu-close>${icon("X")}</button>
        ${links}
        <a class="nav-cta mobile" href="${site.personalDiagnosisUrl}">${ctaLabel}</a>
      </div>
    </header>
  `;
}

function renderHome(): string {
  return `
    <section class="hero-section home-hero">
      <div class="section-inner hero-grid">
        <div class="hero-copy">
          <span class="eyebrow">${hero.eyebrow}</span>
          <h1 class="home-title"><span>看清你的岗位任务，</span><span>先<mark>${hero.titleHighlight}</mark>最该升级的一步</span></h1>
          <p>${hero.text}</p>
          ${renderHeroIntake()}
          <div class="trust-row">${hero.badges.map((item) => `<span>${icon("ShieldCheck")}${item}</span>`).join("")}</div>
        </div>
        ${renderScannerPanel()}
      </div>
    </section>
    <section class="value-strip">
      <div class="section-inner">
        <h2 class="strip-title">${valueSection.title}</h2>
        <div class="value-grid">${valueCards.map(renderValueCard).join("")}</div>
      </div>
    </section>
    ${renderFreeReportPreview()}
    ${renderPackagePlans()}
    ${renderModuleStrip()}
    ${renderAudienceSection()}
    ${renderInsightsTeaser()}
    ${renderCtaBand("现在开始，免费生成你的岗位初筛报告", "2 分钟看清岗位风险，获得第一个 AI 工作流动作，立即行动起来。", "免费测我的岗位", "无需注册 · 隐私安全")}
  `;
}

function renderHeroIntake(): string {
  return `
    <form class="hero-intake" action="${site.personalDiagnosisUrl}" method="get">
      <input name="role" type="text" placeholder="${hero.placeholder}" autocomplete="off" />
      <button type="submit">${hero.inputButton}${icon("ArrowRight", "button-icon")}</button>
    </form>
    <div class="hero-actions">
      <a class="primary-button" href="${site.personalDiagnosisUrl}">${hero.primary}${icon("ArrowRight", "button-icon")}</a>
      <a class="secondary-button" href="#sample-report">${hero.secondary}</a>
    </div>
  `;
}

function renderScannerPanel(): string {
  return `
    <aside class="scanner-panel" aria-label="AI 岗位任务扫描示意">
      <div class="scanner-head">
        <strong>${scannerDemo.title}</strong>
        <span>${scannerDemo.progress}</span>
      </div>
      <div class="progress-bar"><span></span></div>
      <div class="scanner-metrics">
        ${scannerDemo.metrics.map((item) => `<div><span>${item.label}</span><strong>${item.value}</strong></div>`).join("")}
      </div>
      <div class="risk-card">
        ${icon("Shield", "risk-icon")}
        <div>
          <span class="panel-label">${scannerDemo.riskTitle}</span>
          <h3>${scannerDemo.verdict}</h3>
          <p>${scannerDemo.verdictText}</p>
        </div>
        ${renderGauge(scannerDemo.gaugeLabels)}
      </div>
      <div class="workflow-card">
        <span>${icon("Network")}${scannerDemo.workflowTitle}</span>
        <div>
          <strong>${scannerDemo.workflow}</strong>
          <p>${scannerDemo.workflowText}</p>
        </div>
        <a href="#sample-report">查看详情</a>
      </div>
      <small>+ ${scannerDemo.bottomNote}</small>
    </aside>
  `;
}

function renderGauge(labels?: readonly string[]): string {
  return `
    <div class="gauge${labels ? " has-labels" : ""}" aria-label="风险仪表盘">
      <span class="gauge-arc"></span>
      <span class="gauge-needle"></span>
      ${labels ? `<div class="gauge-labels">${labels.map(renderGaugeLabel).join("")}</div>` : ""}
    </div>
  `;
}

function renderGaugeLabel(item: string): string {
  const [label, note] = item.split("：");

  return `<span><strong>${label}</strong>${note ? `<small>${note}</small>` : ""}</span>`;
}

function renderValueCard(item: (typeof valueCards)[number]): string {
  return `
    <article class="value-card ${item.tone}">
      <span>${icon(item.icon)}</span>
      <div>
        <h2>${item.title}</h2>
        <p>${item.text}</p>
      </div>
    </article>
  `;
}

function renderFreeReportPreview(): string {
  return `
    <section class="section report-section">
      <div class="section-inner">
        <div class="section-head">
          <div>
            <h2>${freeReportPreview.sectionTitle}</h2>
            <p>${freeReportPreview.sectionSubtitle}</p>
          </div>
        </div>
        <div class="report-preview-grid">
          <article class="report-card-main">
            <h3>${freeReportPreview.title}</h3>
            <p class="report-profile">${freeReportPreview.profile}</p>
            <div class="risk-summary">
              ${icon("Shield", "risk-icon")}
              <strong>${freeReportPreview.verdict}</strong>
              ${renderGauge(freeReportPreview.gaugeLabels)}
            </div>
            <p class="report-conclusion">${freeReportPreview.conclusion}</p>
            <div class="report-note">
              <h4>${freeReportPreview.conclusionTitle}</h4>
              <strong>${freeReportPreview.conclusionSubtitle}</strong>
              ${renderTextBlocks(freeReportPreview.summary)}
            </div>
            <div class="signal-note">
              <h4>短期内需要关注的变化信号</h4>
              <ul>${freeReportPreview.signals.map((item) => `<li>${item}</li>`).join("")}</ul>
            </div>
          </article>
          <div class="report-side">
            <article>
              <h3>正在贬值的工作环节</h3>
              <div class="detail-card-grid">
                ${freeReportPreview.depreciatingAreas
                  .map(
                    (item) => `
                      <div class="detail-card">
                        <strong>${item.title}</strong>
                        <p>${item.text}</p>
                      </div>
                    `
                  )
                  .join("")}
              </div>
            </article>
            <article>
              <h3>你的工作价值风险排序</h3>
              <div class="risk-rank detailed">
                ${freeReportPreview.riskRanking
                  .map(
                    (item) => `
                      <section class="risk-rank-card ${item.tone}">
                        <h4>${item.title}</h4>
                        <p>${item.text}</p>
                        <ul>${item.items.map((entry) => `<li>${entry}</li>`).join("")}</ul>
                      </section>
                    `
                  )
                  .join("")}
              </div>
            </article>
            <article>
              <h3>${freeReportPreview.workflow.title}</h3>
              <p>${freeReportPreview.workflow.description}</p>
              <div class="workflow-detail">
                <strong>你可以先准备这些材料：</strong>
                <ul>${freeReportPreview.workflow.materials.map((item) => `<li>${item}</li>`).join("")}</ul>
                <p>${freeReportPreview.workflow.advice}</p>
                <div class="workflow-list-grid">
                  <div><strong>让 AI 先帮你整理</strong><ul>${freeReportPreview.workflow.aiHelps.map((item) => `<li>${item}</li>`).join("")}</ul></div>
                  <div><strong>你必须自己判断</strong><ul>${freeReportPreview.workflow.humanJudgment.map((item) => `<li>${item}</li>`).join("")}</ul></div>
                  <div><strong>最后沉淀到</strong><ul>${freeReportPreview.workflow.outputs.map((item) => `<li>${item}</li>`).join("")}</ul></div>
                </div>
                <p class="workflow-conclusion">${freeReportPreview.workflow.conclusion}</p>
              </div>
              <a class="text-link" href="#sample-report">查看完整报告节选${icon("ArrowRight", "button-icon")}</a>
            </article>
          </div>
        </div>
      </div>
    </section>
  `;
}

function renderTextBlocks(text: string): string {
  return text
    .split(/\n\n+/)
    .map((item) => `<p>${item}</p>`)
    .join("");
}

function renderPackagePlans(): string {
  return `
    <section class="section plan-section">
      <div class="section-inner">
        <div class="section-head">
          <div>
            <h2>从免费初筛到完整升级包</h2>
            <p>一次判断，获得体系化的岗位 AI 升级方案。</p>
          </div>
        </div>
        <div class="plan-grid">
          ${packagePlans
            .map(
              (plan, index) => `
                <article class="plan-card ${index === 1 ? "is-featured plan-upgrade" : "plan-free"}">
                  <div class="plan-title">
                    <h3>${plan.name}</h3>
                    <span>${plan.tag}</span>
                  </div>
                  <ul>${plan.items.map((item) => `<li>${icon("ShieldCheck")}${item}</li>`).join("")}</ul>
                  <a class="${index === 1 ? "primary-button" : "secondary-button"}" href="${plan.href}">${plan.cta}${icon("ArrowRight", "button-icon")}</a>
                </article>
              `
            )
            .join("")}
        </div>
      </div>
    </section>
  `;
}

function renderModuleStrip(): string {
  return `
    <section class="section module-section">
      <div class="section-inner module-panel">
        <h2>完整岗位 AI 升级包包含的核心模块</h2>
        <div class="module-grid">
          ${packageModules
            .map(
              (item, index) => `
                <article class="module-card module-card-${index + 1}">
                  ${renderModulePreview(index)}
                  <h3>${item.title}</h3>
                  <p>${item.text}</p>
                </article>
              `
            )
            .join("")}
        </div>
      </div>
    </section>
  `;
}

function renderModulePreview(index: number): string {
  const variants = ["actions", "materials", "prompts", "template", "judgement", "path", "calendar"];
  const variant = variants[index] ?? "actions";

  if (variant === "actions") {
    return `
      <div class="module-preview module-preview-actions" aria-hidden="true">
        <i></i><i></i><i></i>
      </div>
    `;
  }

  if (variant === "materials") {
    return `
      <div class="module-preview module-preview-materials" aria-hidden="true">
        <i></i><i></i><i></i><i></i>
      </div>
    `;
  }

  if (variant === "prompts") {
    return `
      <div class="module-preview module-preview-prompts" aria-hidden="true">
        <b></b><i></i><i></i><i></i>
      </div>
    `;
  }

  if (variant === "template") {
    return `
      <div class="module-preview module-preview-template" aria-hidden="true">
        <i></i><i></i><i></i><i></i><i></i><i></i>
      </div>
    `;
  }

  if (variant === "judgement") {
    return `
      <div class="module-preview module-preview-judgement" aria-hidden="true">
        <i></i><i></i><i></i><i></i>
      </div>
    `;
  }

  if (variant === "path") {
    return `
      <div class="module-preview module-preview-path" aria-hidden="true">
        <i></i><i></i><i></i>
      </div>
    `;
  }

  return `
    <div class="module-preview module-preview-calendar" aria-hidden="true">
      <i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i>
    </div>
  `;
}

function renderAudienceSection(
  title = "适合已经开始用 AI，但还没形成岗位工作流的人",
  cards: readonly { title: string; text: string; icon: string }[] = audienceCards,
  ctaLabel = "了解更多"
): string {
  return `
    <section class="section audience-section">
      <div class="section-inner">
        <div class="section-head">
          <div>
            <h2>${title}</h2>
          </div>
        </div>
        <div class="audience-grid">
          ${cards
            .map(
              (item) => `
                <article class="audience-card">
                  <span>${icon(item.icon)}</span>
                  <div>
                    <h3>${item.title}</h3>
                    <p>${item.text}</p>
                    <a href="${site.personalDiagnosisUrl}">${ctaLabel}${icon("ArrowRight", "button-icon")}</a>
                  </div>
                </article>
              `
            )
            .join("")}
        </div>
      </div>
    </section>
  `;
}

function renderInsightsTeaser(): string {
  return `
    <section class="section insight-preview">
      <div class="section-inner">
        <div class="section-head split">
          <div>
            <h2>岗位升值观察</h2>
            <p>围绕岗位任务价值变化、AI 工作流和个人升级路径，持续沉淀可复用的判断方法。</p>
          </div>
          <a class="text-link" href="#insights">查看岗位观察${icon("ArrowRight", "button-icon")}</a>
        </div>
        <div class="insight-card-grid">${homeInsights.map(renderInsightCard).join("")}</div>
      </div>
    </section>
  `;
}

function renderCareerPage(): string {
  return `
    <section class="hero-section product-hero">
      <div class="section-inner hero-grid">
        <div class="hero-copy">
          <h1 class="career-title"><span>岗位 AI 升值初筛，</span><span>先看清你的</span><span><mark>任务价值变化</mark></span></h1>
          <p>${careerPage.heroText}</p>
          <div class="hero-actions">
            <a class="primary-button" href="${site.personalDiagnosisUrl}">免费开始初筛${icon("ArrowRight", "button-icon")}</a>
            <a class="secondary-button" href="#sample-report">查看样例报告</a>
          </div>
          <div class="trust-row">${careerPage.badges.map((item) => `<span>${icon("ShieldCheck")}${item}</span>`).join("")}</div>
        </div>
        ${renderResultCard()}
      </div>
    </section>
    <section class="section">
      <div class="section-inner info-grid three">
        ${renderInfoCard("你会输入什么", "简单描述 4 项信息", careerPage.inputs, "ClipboardList", "input")}
        ${renderInfoCard("你会得到什么", "一份可行动的初筛结论", careerPage.outputs, "TrendingUp", "output")}
        ${renderInfoCard("为什么这不是普通职业测评", "我们聚焦任务价值变化和可执行落地", careerPage.reasons, "Target", "focus")}
      </div>
    </section>
    <section class="section process-section">
      <div class="section-inner">
        <h2>如何生成你的岗位升值初筛</h2>
        <div class="process-grid">${careerPage.process.map(renderProcessStep).join("")}</div>
      </div>
    </section>
    ${renderAudienceSection(careerPage.audienceTitle, careerPage.audience, "立即测一测")}
    ${renderCareerReportComparison()}
    ${renderFaqSection()}
    ${renderCtaBand("先看清变化，再决定下一步", "2–3 分钟完成你的岗位 AI 升值初筛，获得可执行的第一步工作流。", "免费开始初筛", "无需注册 · 隐私保护")}
  `;
}

function renderResultCard(): string {
  const result = careerPage.result;

  return `
    <aside class="result-panel">
      <div class="panel-top">
        <h2>${result.title}</h2>
        <span>${result.tag}</span>
      </div>
      <div class="result-risk">
        ${icon("Shield", "risk-icon")}
        <strong>${result.verdict}</strong>
        <p>${result.description}</p>
        ${renderGauge(["低风险", "中风险", "高风险"])}
      </div>
      <div class="result-stat-grid">
        ${result.metrics.map((item) => `<span><b>${item.value}</b>${item.label}<small>${item.note}</small></span>`).join("")}
      </div>
      <p class="result-footer">${result.footer}</p>
      <a href="#contact">${result.link}${icon("ArrowRight", "button-icon")}</a>
    </aside>
  `;
}

function renderInfoCard(
  title: string,
  subtitle: string,
  items: readonly string[],
  iconName: string,
  variant: "input" | "output" | "focus"
): string {
  return `
    <article class="info-card info-card-${variant}">
      <div class="info-card-copy">
        <span>${icon(iconName)}</span>
        <h2>${title}</h2>
        <p>${subtitle}</p>
        <ul>${items.map((item) => `<li>${item}</li>`).join("")}</ul>
      </div>
      ${renderInfoVisual(variant)}
    </article>
  `;
}

function renderInfoVisual(variant: "input" | "output" | "focus"): string {
  if (variant === "input") {
    return `
      <div class="info-visual info-visual-input" aria-hidden="true">
        <span class="doc-sheet">
          <i></i><i></i><i></i><i></i>
        </span>
        <span class="doc-pen">${icon("Pencil")}</span>
      </div>
    `;
  }

  if (variant === "output") {
    return `
      <div class="info-visual info-visual-output" aria-hidden="true">
        <span class="mini-chart">
          <i></i><i></i><i></i><i></i>
        </span>
        ${icon("TrendingUp", "visual-line-icon")}
      </div>
    `;
  }

  return `
    <div class="info-visual info-visual-focus" aria-hidden="true">
      <span class="target-rings"><i></i><i></i><i></i></span>
      <span class="target-dot"></span>
      ${icon("Target", "visual-target-icon")}
    </div>
  `;
}

function renderProcessStep(item: (typeof careerPage.process)[number], index: number): string {
  return `
    <article class="process-card">
      <strong>${index + 1}</strong>
      <div class="process-visual process-visual-${index + 1}" aria-hidden="true">
        <span>${icon(item.icon)}</span>
        ${renderProcessVisual(index)}
      </div>
      <h3>${item.title}</h3>
      <p>${item.text}</p>
    </article>
  `;
}

function renderProcessVisual(index: number): string {
  if (index === 0) {
    return `<div class="process-doc-lines"><i></i><i></i><i></i></div>`;
  }

  if (index === 1) {
    return `<div class="process-node-map"><i></i><i></i><i></i><i></i></div>`;
  }

  if (index === 2) {
    return `<div class="process-bars"><i></i><i></i><i></i></div>`;
  }

  return `<div class="process-flow-mini"><i></i><i></i><i></i></div>`;
}

function renderCareerReportComparison(): string {
  return `
    <section class="section">
      <div class="section-inner report-compare">
        <h2>报告预览（免费版 vs 完整版）</h2>
        <div class="compare-panels">
          <article class="compare-card free-report-card">
            <div class="compare-copy">
              <h3>免费版报告（示例）</h3>
              <span class="report-tag">${careerPage.reportPreview.freeTag}</span>
              <ul>${samplePage.freeHighlights.map((item) => `<li>${icon("ShieldCheck")}${item}</li>`).join("")}</ul>
            </div>
            ${renderFreeReportThumb()}
          </article>
          <article class="compare-card full-report-card">
            <div class="compare-copy">
              <h3>完整版报告（升级解锁）</h3>
              <span class="report-tag">${careerPage.reportPreview.paidTag}</span>
              <ul>${samplePage.paidHighlights.map((item) => `<li>${icon("ShieldCheck")}${item}</li>`).join("")}</ul>
            </div>
            ${renderFullReportThumb()}
          </article>
        </div>
        <div class="report-compare-foot">
          <p>${icon("ShieldCheck")}${careerPage.reportPreview.footer}</p>
          <a class="text-link" href="#sample-report">${careerPage.reportPreview.link}${icon("ArrowRight", "button-icon")}</a>
        </div>
      </div>
    </section>
  `;
}

function renderFreeReportThumb(): string {
  return `
    <div class="report-thumb free-report-thumb" aria-hidden="true">
      <div class="mini-report-page">
        <div class="mini-report-head">
          <span>岗位 AI 升值初筛报告</span>
          <i></i>
        </div>
        <div class="mini-risk-preview">
          <strong>中风险</strong>
          ${renderGauge(["低风险", "中风险", "高风险"])}
        </div>
        <div class="mini-report-note">
          <i></i><i></i><i></i>
        </div>
        <div class="mini-report-boxes">
          <i></i><i></i>
        </div>
      </div>
    </div>
  `;
}

function renderFullReportThumb(): string {
  return `
    <div class="report-thumb full-report-thumb" aria-hidden="true">
      <span class="full-sheet sheet-a">
        <i></i><i></i><i></i><i></i>
      </span>
      <span class="full-sheet sheet-b">
        <i></i><i></i><i></i><i></i><i></i>
      </span>
      <span class="full-sheet sheet-c">
        <b></b><i></i><i></i><i></i>
      </span>
    </div>
  `;
}

function renderFaqSection(): string {
  return `
    <section class="section faq-section">
      <div class="section-inner">
        <h2>常见问题</h2>
        <div class="faq-grid">
          ${careerPage.faqs
            .map(
              (item) => `
                <article class="faq-card">
                  ${icon("Info")}
                  <h3>${item.question}</h3>
                  <p>${item.answer}</p>
                </article>
              `
            )
            .join("")}
        </div>
      </div>
    </section>
  `;
}

function renderSampleReportPage(): string {
  return `
    <section class="hero-section sample-hero">
      <div class="section-inner hero-grid">
        <div class="hero-copy">
          <h1>${highlightText(samplePage.heroTitle, "升级包")}</h1>
          <p>${samplePage.heroText}</p>
          <div class="sample-benefits">
            ${samplePage.benefits.map((item) => `<span>${icon(item.icon)}<strong>${item.title}</strong>${item.text}</span>`).join("")}
          </div>
        </div>
        <div class="document-hero" aria-hidden="true">${renderGauge(["低风险", "中风险", "高风险"])}<span></span><span></span></div>
      </div>
    </section>
    <section class="section sample-detail-section">
      <div class="section-inner sample-columns">
        <article class="sample-sheet free">
          <h2>${samplePage.freeTitle}</h2>
          <p>${samplePage.freeSubtitle}</p>
          ${renderFreeReportPreviewCard()}
        </article>
        <article class="sample-sheet paid">
          <h2>${samplePage.paidTitle}</h2>
          <p>${samplePage.paidSubtitle}</p>
          ${renderFullPackagePreview()}
        </article>
      </div>
    </section>
    <section class="section">
      <div class="section-inner comparison-table">
        <h2>免费版 vs 完整版 对比</h2>
        <div class="comparison-body">
          <table>
            <thead><tr><th>对比维度</th><th>免费版（岗位初筛）</th><th>完整版（AI 升级包）</th></tr></thead>
            <tbody>${samplePage.comparison.map((row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join("")}</tr>`).join("")}</tbody>
          </table>
          <aside class="comparison-suggestion">${icon("ShieldCheck")}<h3>建议使用路径</h3><p>${samplePage.suggestion}</p></aside>
        </div>
      </div>
    </section>
    <section class="section dual-cta sample-dual-cta">
      <div class="section-inner dual-cta-grid">
        <article>
          <h2>先体验，心中有数</h2>
          <p>免费获取岗位初筛报告，了解你的 AI 风险等级与关键变化。</p>
          <div class="dual-action-row">
            <a class="primary-button" href="${site.personalDiagnosisUrl}">免费开始初筛${icon("ArrowRight", "button-icon")}</a>
            <small>3 分钟完成</small>
          </div>
        </article>
        <article>
          <h2>想系统升级，真正落地</h2>
          <p>获取完整升级包，掌握可执行的 AI 升值方案与工具。</p>
          <div class="dual-action-row">
            <a class="primary-button" href="#upgrade-reservation">预约完整升级包${icon("ArrowRight", "button-icon")}</a>
            <small>深度方案</small>
          </div>
        </article>
        <div class="dual-cta-visual" aria-hidden="true">${renderGauge(["低", "中", "高"])}<span></span></div>
      </div>
    </section>
  `;
}

function renderUpgradeReservationPage(): string {
  return `
    <section class="section upgrade-reservation-page">
      <div class="section-inner upgrade-reservation-hero">
        <div class="upgrade-hero-copy">
          <a class="text-link back-link" href="#sample-report">${icon("ArrowRight", "button-icon")}返回样例报告</a>
          <h1>你的完整岗位 AI 升级包</h1>
          <p>你已经完成岗位升值初筛。完整版会继续补齐更完整的岗位 AI 上手动作。</p>
        </div>
        <form class="upgrade-reservation-form" data-upgrade-form novalidate>
          <label>
            <span>邮箱</span>
            <input name="email" type="email" autocomplete="email" placeholder="请输入你的邮箱" />
            <small data-upgrade-error></small>
          </label>
          <button class="submit-button" type="submit">提交预约</button>
          <p>仅用于通知完整版上线，不会公开你的信息。</p>
          <p class="form-status" data-upgrade-status role="status"></p>
        </form>
      </div>
      <div class="section-inner upgrade-intro-card">
        <h2>完整版会帮你进一步生成：</h2>
        <p>你这个岗位最该先做的 3 个 AI 上手动作，包括要准备什么材料、怎么分批交给 AI、让 AI 怎么分析、最后产出什么，以及如何把这些结果沉淀成你的职业资产。</p>
      </div>
      <div class="section-inner upgrade-compare-grid">
        <article>
          <h2>免费版</h2>
          <ul>
            ${samplePage.freeHighlights.map((item) => `<li>${item}</li>`).join("")}
          </ul>
        </article>
        <article class="is-featured">
          <h2>完整版</h2>
          <ul>
            ${samplePage.paidHighlights.map((item) => `<li>${item}</li>`).join("")}
          </ul>
        </article>
      </div>
    </section>
  `;
}

function renderFreeReportPreviewCard(): string {
  return `
    <div class="mini-report">
      <div class="risk-summary">
        ${icon("Shield", "risk-icon")}
        <strong>${freeReportPreview.verdict}</strong>
        ${renderGauge(freeReportPreview.gaugeLabels)}
      </div>
      <p class="report-conclusion">中风险：${freeReportPreview.conclusion}</p>
      <h3>${freeReportPreview.conclusionTitle}</h3>
      ${renderTextBlocks(freeReportPreview.summary)}
      <h3>正在贬值的工作环节</h3>
      <div class="detail-card-grid">${freeReportPreview.depreciatingAreas.map((item) => `<div class="detail-card"><strong>${item.title}</strong><p>${item.text}</p></div>`).join("")}</div>
      <h3>你的工作价值风险排序</h3>
      <div class="risk-rank detailed">${freeReportPreview.riskRanking.map((item) => `<section class="risk-rank-card ${item.tone}"><h4>${item.title}</h4><ul>${item.items.map((entry) => `<li>${entry}</li>`).join("")}</ul></section>`).join("")}</div>
      <h3>短期内需要关注的变化信号</h3>
      <ul>${freeReportPreview.signals.map((item) => `<li>${item}</li>`).join("")}</ul>
      <h3>${freeReportPreview.workflow.title}</h3>
      <p>${freeReportPreview.workflow.description}</p>
      <strong>你可以先准备这些材料：</strong>
      <ul>${freeReportPreview.workflow.materials.map((item) => `<li>${item}</li>`).join("")}</ul>
      <strong>最后沉淀到：</strong>
      <ul>${freeReportPreview.workflow.outputs.map((item) => `<li>${item}</li>`).join("")}</ul>
      <p class="workflow-conclusion">${freeReportPreview.workflow.conclusion}</p>
    </div>
  `;
}

function renderFullPackagePreview(): string {
  const preview = samplePage.contentPreviews;

  return `
    <div class="package-preview">
      <section class="package-module-panel">
        <h3>${icon("ShieldCheck")}完整报告包含以下核心模块</h3>
      <div class="package-module-grid">
        ${samplePage.modules.map((item) => `<span>${icon(item.icon)}<strong>${item.title}</strong><small>${item.text}</small></span>`).join("")}
      </div>
      </section>
      <h3>报告内容预览（节选）</h3>
      <div class="report-cards-small">
        <article class="action-preview"><h4>AI 上手动作（示例）</h4>${preview.actions.map((item) => `<p><strong>${item.title}</strong><br />${item.text}</p>`).join("")}<a>查看更多动作预览</a></article>
        <article class="materials-preview"><h4>材料清单（示例）</h4><ul>${preview.materials.map((item) => `<li>${item}</li>`).join("")}</ul><a>查看更多材料清单</a></article>
        <article class="dark-preview"><h4>AI 指令链（示例）</h4><ol><li>${preview.prompt}</li></ol><a>查看更多指令链</a></article>
        <article class="template-preview"><h4>输出模板（示例）</h4><strong>选题机会评估表</strong><div class="template-table">${preview.templateFields.map((item) => `<span>${item}</span>`).join("")}</div><a>查看更多模板</a></article>
        <article class="judgment-preview"><h4>人工判断点（示例）</h4><ul>${preview.judgment.map((item) => `<li>${icon("ShieldCheck")}${item}</li>`).join("")}</ul><a>查看更多判断点</a></article>
        <article class="plan-preview"><h4>7 天启动计划（示例）</h4><ul>${preview.plan.map((item) => `<li>${item}</li>`).join("")}</ul><a>查看完整计划</a></article>
      </div>
      <h3>岗位升值路径（示例）</h3>
      <div class="path-map">
        ${preview.path.map((item) => `<span>${item}</span><b></b>`).join("")}
      </div>
      <p class="current-path">当前定位：${preview.current}</p>
    </div>
  `;
}

function renderInsightsPage(): string {
  const [featured] = insights;

  return `
    <section class="hero-section insight-hero">
      <div class="section-inner hero-grid">
        <div class="hero-copy">
          <span class="eyebrow">AI 岗位升值观察</span>
          <h1>岗位<mark>升值</mark>观察</h1>
          <p>记录 AI 如何改变任务价值与工作方法，提供可验证、可复用的观察与实战启发。</p>
        </div>
        <div class="insight-visual" aria-hidden="true"><span></span><strong></strong><em></em></div>
      </div>
    </section>
    <section class="section">
      <div class="section-inner">
        <a class="featured-article" href="${featured.href}">
          ${renderArticleArt(featured.image)}
          <div>
            <span class="tag">${featured.category}</span>
            <h2>${featured.title}</h2>
            <p>${featured.summary}</p>
            <div class="article-meta">
              <span>${featured.date}</span><span>${featured.reads}</span><span>${featured.readTime}</span>
            </div>
          </div>
        </a>
      </div>
    </section>
  `;
}

function renderInsightCard(item: { category: string; title: string; summary: string; image: string; href: string }): string {
  return `
    <article class="insight-card">
      ${renderArticleArt(item.image)}
      <span class="tag">${item.category}</span>
      <h3>${item.title}</h3>
      <p>${item.summary}</p>
      <a class="text-link" href="${item.href}">阅读文章${icon("ArrowRight", "button-icon")}</a>
    </article>
  `;
}

function renderArticleArt(type: string): string {
  return `
    <div class="article-art art-${type}" aria-hidden="true">
      <span></span><span></span><span></span>
    </div>
  `;
}

function renderArticlePage(): string {
  const related = insights.slice(1, 5);

  return `
    <section class="page-section article-page">
      <div class="section-inner article-layout ${related.length === 0 ? "single-column" : ""}">
        <article class="article-main">
          ${renderBreadcrumb(["首页", "岗位观察", article.category])}
          <span class="tag">${article.category}</span>
          <h1>${article.title}</h1>
          <div class="article-meta rich">
            <span>${icon("CalendarDays")}${article.date}</span>
            <span>${icon("Tag")}${article.author}</span>
            <span>${article.readTime}</span>
          </div>
          <div class="article-hero-art">${renderArticleArt("dashboard")}</div>
          <p class="article-intro">${article.intro}</p>
          ${article.sections
            .map(
              (section, index) => `
                <section class="article-block">
                  <span>${section.index}</span>
                  <div>
                    <h2>${section.title}</h2>
                    <p>${section.text}</p>
                    ${index === 1 ? `<blockquote>${article.quote}<cite>知汐 Labs</cite></blockquote>` : ""}
                  </div>
                </section>
              `
            )
            .join("")}
        </article>
        ${
          related.length > 0
            ? `<aside class="related-card">
          <h2>相关文章</h2>
          ${related
              .map(
                (item) => `
                <a href="${item.href}">
                  ${renderArticleArt(item.image)}
                  <span>${item.title}</span>
                  <small>${item.readTime}</small>
                </a>
              `
              )
              .join("")}
          <a class="text-link all-link" href="#insights">查看全部文章${icon("ArrowRight", "button-icon")}</a>
        </aside>`
            : ""
        }
      </div>
    </section>
    ${renderCtaBand("想知道你的岗位任务价值如何变化？", "从一次免费岗位初筛开始，先看清真实任务的价值变化。", "免费测我的岗位")}
  `;
}

function renderAboutPage(): string {
  return `
    <section class="hero-section about-hero">
      <div class="section-inner hero-grid">
        <div class="hero-copy">
          <h1>关于知汐</h1>
          <p>知汐 Labs 关注 AI 如何改变真实工作中的任务价值，帮助个人把下一步变成可执行的工作流。</p>
          <div class="stats-row">${aboutPage.stats.map((item) => `<span>${icon(item.icon)}<strong>${item.value}</strong>${item.label}</span>`).join("")}</div>
        </div>
        <div class="about-visual" aria-hidden="true"><img src="${site.logo}" alt="" /><span></span><span></span></div>
      </div>
    </section>
    <section class="section about-blocks">
      <div class="section-inner two-column">
        <div>
          <h2><span>1</span>我们在做什么</h2>
          <p>我们通过岗位任务价值分析，理解 AI 时代不同岗位的任务变化，识别适合工具辅助、流程优化和能力升级的环节，并将其转化为个人可落地的行动建议与工作流。</p>
          <div class="mini-tile-grid">${aboutPage.doing.map((item) => renderMiniTile(item.title, item.text, item.icon)).join("")}</div>
        </div>
        <div>
          <h2><span>2</span>为什么从任务开始，而不是从工具开始</h2>
          <p>工具很多，真正的难点是：哪些任务值得被 AI 改变？我们先看清任务的价值变化，再决定用什么方式、在哪一步介入。</p>
          <div class="vs-box">
            <article><h3>从工具开始</h3><ul><li>功能导向，容易被营销牵引</li><li>关注“能做什么”，而非“该做什么”</li><li>结果：尝试多，落地少，效果不稳定</li></ul></article>
            <strong>VS</strong>
            <article><h3>从任务开始</h3><ul><li>价值导向，聚焦真实工作问题</li><li>关注“值得改变什么”与“如何改变”</li><li>结果：投入更少，产生可衡量的回报</li></ul></article>
          </div>
        </div>
      </div>
    </section>
    <section class="section">
      <div class="section-inner">
        <h2 class="number-title"><span>3</span>我们相信什么</h2>
        <div class="belief-grid">${aboutPage.beliefs.map((item) => renderBeliefCard(item.title, item.text, item.icon)).join("")}</div>
      </div>
    </section>
    <section class="section">
      <div class="section-inner">
        <h2 class="number-title"><span>4</span>我们的方法</h2>
        <div class="method-row">${aboutPage.method.map((item) => renderMethodStep(item.title, item.text, item.icon)).join("")}</div>
      </div>
    </section>
    <section class="section about-lower">
      <div class="section-inner two-column">
        <div>
          <h2><span>5</span>适合谁</h2>
          <div class="people-list">${aboutPage.audience.map((item, index) => `<article><span class="person-avatar avatar-${index + 1}" aria-hidden="true"><i></i></span><h3>${item.title}</h3><p>${item.text}</p></article>`).join("")}</div>
        </div>
        <div>
          <h2><span>6</span>未来方向</h2>
          <p>当前阶段，我们专注于个人岗位产品的打磨与体验优化，同时持续探索更广泛的 AI + 工作流解决方案。</p>
          <div class="future-pills">${aboutPage.futureDirections.map((item, index) => `<span>${icon(["UserRound", "ShieldCheck", "Target"][index] ?? "ShieldCheck")}${item}</span>`).join("")}</div>
          <div class="future-card"><div><strong>持续探索</strong><p>个人岗位产品为当前阶段重点。我们将逐步扩展到团队协作、组织流程等更广阔的场景，帮助更多人在 AI 时代获得更大的价值。</p></div><span class="future-rocket" aria-hidden="true"><i></i></span></div>
        </div>
      </div>
    </section>
    ${renderCtaBand("现在开始，免费看清你的岗位价值变化", "只需 3 分钟，获取你的岗位初筛报告与下一步建议。", "免费尝试岗位初筛", "无需注册 · 隐私保护")}
  `;
}

function renderMiniTile(title: string, text: string, iconName: string): string {
  return `<article class="mini-tile">${icon(iconName)}<h3>${title}</h3><p>${text}</p></article>`;
}

function renderBeliefCard(title: string, text: string, iconName: string): string {
  return `<article class="belief-card"><div>${icon(iconName)}</div><h3>${title}</h3><p>${text}</p></article>`;
}

function renderMethodStep(title: string, text: string, iconName: string): string {
  return `<article class="method-step">${icon(iconName)}<h3>${title}</h3><p>${text}</p></article>`;
}

function renderContactPage(): string {
  return `
    <section class="hero-section contact-hero">
      <div class="section-inner hero-grid">
        <div class="hero-copy">
          <h1>联系<mark>知汐</mark></h1>
          <p>无论是问题咨询、反馈建议、商务合作还是报告相关支持，我们都很乐意为你提供帮助。</p>
        </div>
        <div class="contact-visual" aria-hidden="true"><span></span><strong></strong><em></em></div>
      </div>
    </section>
    <section class="section contact-section">
      <div class="section-inner contact-grid">
        <article class="contact-panel">
          <h2>给我们留言</h2>
          <p>请填写以下信息，我们会尽快与您联系。</p>
          ${renderContactForm()}
        </article>
        <aside class="topic-panel">
          <h2>选择联系主题</h2>
          <p>选择以下主题，我们将把你的留言转交给最合适的团队。</p>
          <div class="topic-list">${contactTopics.map((item) => `<article>${icon(item.icon)}<div><h3>${item.title}</h3><p>${item.text}</p></div>${icon("ArrowRight", "button-icon")}</article>`).join("")}</div>
        </aside>
      </div>
    </section>
  `;
}

function renderContactForm(): string {
  return `
    <form class="contact-form" data-contact-form novalidate>
      <label>
        <span>你的姓名 <b>*</b></span>
        <input name="name" type="text" autocomplete="name" placeholder="请输入你的姓名" />
        <small data-error-for="name"></small>
      </label>
      <label>
        <span>联系方式 <b>*</b></span>
        <input name="contact" type="text" autocomplete="email" placeholder="请输入邮箱地址" />
        <small data-error-for="contact"></small>
      </label>
      <label>
        <span>你的岗位</span>
        <input name="company" type="text" autocomplete="organization-title" placeholder="请输入你的岗位" />
        <small data-error-for="company"></small>
      </label>
      <label>
        <span>你想咨询的问题 <b>*</b></span>
        <select name="direction">
          <option value="">请选择</option>
          ${contactDirections.map((item) => `<option value="${item.value}">${item.label}</option>`).join("")}
        </select>
        <small data-error-for="direction"></small>
      </label>
      <label>
        <span>留言内容 <b>*</b></span>
        <textarea name="message" maxlength="1000" placeholder="请详细描述您的问题或需求"></textarea>
        <em><span data-message-count>0</span>/1000</em>
        <small data-error-for="message"></small>
      </label>
      <label class="consent-row">
        <input name="consent" type="checkbox" />
        <span>我已阅读并同意 <a href="#terms">用户协议</a> 和 <a href="#privacy">隐私政策</a></span>
      </label>
      <small data-error-for="consent"></small>
      <button class="submit-button" type="submit">提交留言</button>
      <p class="form-privacy-tip">${icon("ShieldCheck")}我们会对您的信息严格保密，仅用于回复您的咨询。</p>
      <p class="form-status" data-form-status role="status"></p>
    </form>
  `;
}

function renderLegalPage(kind: keyof typeof legalDocuments): string {
  const doc = legalDocuments[kind];

  return `
    <section class="legal-hero ${kind}">
      <div class="section-inner legal-hero-grid">
        <div>
          <span class="legal-icon">${icon(doc.icon)}</span>
          <h1>${doc.title}</h1>
          <p>${kind === "privacy" ? "知汐 Labs 非常重视用户的隐私与个人信息保护。" : "欢迎使用知汐 Labs 提供的产品与服务。"}</p>
        </div>
        <div class="legal-visual" aria-hidden="true">${icon(doc.icon)}</div>
      </div>
    </section>
    <section class="page-section legal-page">
      <div class="section-inner legal-layout">
        <article class="legal-document">
          ${kind === "terms" ? renderBreadcrumb(["首页", doc.label]) : ""}
          ${renderMarkdown(doc.markdown)}
        </article>
      </div>
    </section>
  `;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function renderInlineMarkdown(value: string): string {
  return escapeHtml(value.trim())
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replaceAll("《隐私政策》", '<a href="#privacy">《隐私政策》</a>');
}

function renderMarkdown(markdown: string): string {
  const lines = markdown.split(/\r?\n/);
  const html: string[] = [];
  let paragraph: string[] = [];
  let listType: "ul" | "ol" | null = null;
  let sectionOpen = false;
  let h2Index = 0;

  const closeParagraph = () => {
    if (paragraph.length === 0) {
      return;
    }

    html.push(`<p>${paragraph.map(renderInlineMarkdown).join("<br />")}</p>`);
    paragraph = [];
  };

  const closeList = () => {
    if (!listType) {
      return;
    }

    html.push(`</${listType}>`);
    listType = null;
  };

  const closeSection = () => {
    closeParagraph();
    closeList();
    if (sectionOpen) {
      html.push("</section>");
      sectionOpen = false;
    }
  };

  const openList = (type: "ul" | "ol") => {
    if (listType === type) {
      return;
    }

    closeList();
    html.push(`<${type}>`);
    listType = type;
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) {
      closeParagraph();
      closeList();
      return;
    }

    const heading = /^(#{1,3})\s+(.+)$/.exec(line);
    if (heading) {
      closeParagraph();
      closeList();
      const level = Math.min(heading[1].length, 3);

      if (level === 2) {
        closeSection();
        h2Index += 1;
        html.push(`<section class="legal-section-card" id="legal-section-${h2Index}"><h2>${renderInlineMarkdown(heading[2])}</h2>`);
        sectionOpen = true;
        return;
      }

      html.push(`<h${level}>${renderInlineMarkdown(heading[2])}</h${level}>`);
      return;
    }

    const unordered = /^-\s+(.+)$/.exec(line);
    if (unordered) {
      closeParagraph();
      openList("ul");
      html.push(`<li>${renderInlineMarkdown(unordered[1])}</li>`);
      return;
    }

    const ordered = /^\d+\.\s+(.+)$/.exec(line);
    if (ordered) {
      closeParagraph();
      openList("ol");
      html.push(`<li>${renderInlineMarkdown(ordered[1])}</li>`);
      return;
    }

    closeList();
    paragraph.push(line);
  });

  closeSection();

  return html.join("");
}

function renderBreadcrumb(items: string[]): string {
  return `<nav class="breadcrumb" aria-label="面包屑">${items.map((item) => `<span>${item}</span>`).join("<b>/</b>")}</nav>`;
}

function renderCtaBand(title: string, text: string, label: string, helper?: string): string {
  return `
    <section class="section cta-section">
      <div class="section-inner cta-band">
        <span class="cta-mark" aria-hidden="true">${icon("Box")}</span>
        <div class="cta-copy">
          <h2>${title}</h2>
          <p>${text}</p>
        </div>
        <div class="cta-action">
          <a class="primary-button" href="${site.personalDiagnosisUrl}">${label}${icon("ArrowRight", "button-icon")}</a>
          ${helper ? `<small>${helper}</small>` : ""}
        </div>
      </div>
    </section>
  `;
}

function highlightText(text: string, keyword: string): string {
  return escapeHtml(text).replace(keyword, `<mark>${keyword}</mark>`);
}

function renderCurrentPage(route: string): string {
  if (route === "career-upgrade") {
    return renderCareerPage();
  }

  if (route === "sample-report") {
    return renderSampleReportPage();
  }

  if (route === "upgrade-reservation") {
    return renderUpgradeReservationPage();
  }

  if (route === "insights") {
    return renderInsightsPage();
  }

  if (route === "article-task-value-change") {
    return renderArticlePage();
  }

  if (route === "about") {
    return renderAboutPage();
  }

  if (route === "contact") {
    return renderContactPage();
  }

  if (route === "terms") {
    return renderLegalPage("terms");
  }

  if (route === "privacy") {
    return renderLegalPage("privacy");
  }

  return renderHome();
}

function renderFooter(): string {
  return `
    <footer class="site-footer">
      <div class="section-inner footer-grid">
        <div class="footer-brand-block">
          <a class="brand footer-brand" href="#home">${renderLogo()}</a>
          <p>知汐 Labs 关注 AI 进入真实工作后的岗位任务变化，帮助个人把下一步变成可执行工作流。</p>
          <div class="social-row"><span></span><span></span><span></span></div>
        </div>
        ${footerGroups
          .map(
            (group) => `
              <nav aria-label="${group.title}">
                <h2>${group.title}</h2>
                ${group.links.map((item) => `<a href="${item.href}">${item.label}</a>`).join("")}
              </nav>
            `
          )
          .join("")}
        <div class="beian-links" aria-label="备案信息">
          <a class="icp-link" href="${site.icpUrl}" target="_blank" rel="noreferrer">${site.icp}</a>
          <a class="police-beian-link" href="${site.policeBeianUrl}" target="_blank" rel="noreferrer">
            <img src="${site.policeBeianIcon}" alt="" />
            <span>${site.policeBeian}</span>
          </a>
        </div>
      </div>
      <div class="section-inner footer-bottom">
        <span>© 2025 知汐 Labs. 保留所有权利。</span>
      </div>
    </footer>
  `;
}

function mountIcons(): void {
  createIcons({
    icons: {
      ArrowRight,
      Box,
      Briefcase,
      CalendarDays,
      ClipboardList,
      Code2,
      FilePenLine,
      Info,
      LineChart,
      Mail,
      Menu,
      MessageSquare,
      Network,
      Pencil,
      PieChart,
      Route,
      Search,
      Shield,
      ShieldCheck,
      Tag,
      Target,
      TrendingDown,
      TrendingUp,
      UserRound,
      Users,
      X
    }
  });
}

function wireNavigation(): void {
  const panel = document.querySelector<HTMLElement>("[data-mobile-panel]");
  const openButton = document.querySelector<HTMLButtonElement>("[data-menu-toggle]");
  const closeButton = document.querySelector<HTMLButtonElement>("[data-menu-close]");

  openButton?.addEventListener("click", () => {
    panel?.classList.add("is-open");
    openButton.setAttribute("aria-expanded", "true");
  });

  closeButton?.addEventListener("click", () => {
    panel?.classList.remove("is-open");
    openButton?.setAttribute("aria-expanded", "false");
  });

  panel?.querySelectorAll("a").forEach((link) => {
    link.addEventListener("click", () => {
      panel.classList.remove("is-open");
      openButton?.setAttribute("aria-expanded", "false");
    });
  });
}

function collectFormPayload(form: HTMLFormElement): ContactPayload {
  const data = new FormData(form);

  return {
    name: String(data.get("name") ?? ""),
    company: String(data.get("company") ?? ""),
    contact: String(data.get("contact") ?? ""),
    direction: String(data.get("direction") ?? ""),
    message: String(data.get("message") ?? ""),
    consent: data.get("consent") === "on"
  };
}

function wireContactForm(): void {
  const form = document.querySelector<HTMLFormElement>("[data-contact-form]");
  const message = form?.querySelector<HTMLTextAreaElement>('textarea[name="message"]');
  const counter = form?.querySelector<HTMLElement>("[data-message-count]");
  const status = form?.querySelector<HTMLElement>("[data-form-status]");

  message?.addEventListener("input", () => {
    if (counter) {
      counter.textContent = String(message.value.length);
    }
  });

  form?.addEventListener("submit", (event) => {
    event.preventDefault();

    const payload = collectFormPayload(form);
    const result = validateContactForm(payload);

    form.querySelectorAll("[data-error-for]").forEach((errorNode) => {
      errorNode.textContent = "";
    });

    Object.entries(result.errors).forEach(([field, error]) => {
      const target = form.querySelector<HTMLElement>(`[data-error-for="${field}"]`);
      if (target) {
        target.textContent = error;
      }
    });

    if (!status) {
      return;
    }

    if (!result.valid) {
      status.textContent = "请先修正表单中的提示项。";
      status.className = "form-status is-error";
      return;
    }

    status.textContent = "已完成本地校验。正式上线接入后端前，本页不会上传这份信息。";
    status.className = "form-status is-success";
    form.reset();

    if (counter) {
      counter.textContent = "0";
    }
  });
}

function wireUpgradeReservationForm(): void {
  const form = document.querySelector<HTMLFormElement>("[data-upgrade-form]");
  const email = form?.querySelector<HTMLInputElement>('input[name="email"]');
  const error = form?.querySelector<HTMLElement>("[data-upgrade-error]");
  const status = form?.querySelector<HTMLElement>("[data-upgrade-status]");

  form?.addEventListener("submit", (event) => {
    event.preventDefault();

    const message = validateUpgradeReservationEmail(email?.value ?? "");

    if (error) {
      error.textContent = message;
    }

    if (!status) {
      return;
    }

    if (message) {
      status.textContent = "请先修正邮箱后再提交。";
      status.className = "form-status is-error";
      return;
    }

    status.textContent = "已完成本地校验。正式上线接入后端前，本页不会上传这份信息。";
    status.className = "form-status is-success";
    form.reset();
  });
}

function renderApp(): void {
  const route = currentRoute();

  syncDocumentMetadata(route);

  root.innerHTML = `
    ${renderNav(route)}
    <main>${renderCurrentPage(route)}</main>
    ${renderFooter()}
  `;

  mountIcons();
  wireNavigation();
  wireContactForm();
  wireUpgradeReservationForm();
  window.scrollTo(0, 0);
}

window.addEventListener("hashchange", renderApp);
renderApp();
